import { zodResolver } from "@hookform/resolvers/zod";
import { type SetupInput, setupSchema } from "@shelflist/shared";
import { useMutation } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { useForm } from "react-hook-form";
import { Trans, useTranslation } from "react-i18next";
import { Field, fieldA11y } from "@/components/field";
import { FormError } from "@/components/form-error";
import { PublicLayout } from "@/components/public-layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { api, call } from "@/lib/api";
import { applyFieldErrors, useErrorMessage } from "@/lib/errors";
import { useAfterSignIn } from "@/lib/session";

export const Route = createFileRoute("/setup")({
  component: SetupPage,
});

const FIELDS = ["setupCode", "name", "email", "password"] as const;

function SetupPage() {
  const { t } = useTranslation(["auth", "common"]);
  const errorMessage = useErrorMessage();
  const afterSignIn = useAfterSignIn();
  const form = useForm<SetupInput>({
    resolver: zodResolver(setupSchema),
    defaultValues: { setupCode: "", name: "", email: "", password: "" },
  });
  const setup = useMutation({
    mutationFn: (values: SetupInput) => call(api.setup.$post({ json: values })),
    meta: { handlesErrors: true },
    onSuccess: () => afterSignIn("/"),
    onError: (error) => {
      applyFieldErrors(error, form.setError, FIELDS);
    },
  });
  const { errors } = form.formState;

  return (
    <PublicLayout title={t("setup.title")} description={t("setup.description")}>
      <form
        noValidate
        className="grid gap-4"
        onSubmit={form.handleSubmit((values) => setup.mutate(values))}
      >
        <FormError message={setup.error ? errorMessage(setup.error) : null} />
        <Field
          id="setupCode"
          label={t("setup.code")}
          error={errors.setupCode?.message}
          hint={
            <Trans
              t={t}
              i18nKey="setup.codeHint"
              components={{ code: <code className="bg-muted rounded px-1 py-0.5 text-xs" /> }}
            />
          }
        >
          <Input
            {...fieldA11y("setupCode", errors.setupCode?.message)}
            autoComplete="off"
            spellCheck={false}
            className="font-mono uppercase"
            placeholder="XXXX-XXXX-XXXX-XXXX-XXXX-XXXX"
            autoFocus
            {...form.register("setupCode")}
          />
        </Field>
        <Field id="name" label={t("common:fields.name")} error={errors.name?.message}>
          <Input
            {...fieldA11y("name", errors.name?.message)}
            autoComplete="name"
            {...form.register("name")}
          />
        </Field>
        <Field id="email" label={t("common:fields.email")} error={errors.email?.message}>
          <Input
            {...fieldA11y("email", errors.email?.message)}
            type="email"
            autoComplete="username"
            {...form.register("email")}
          />
        </Field>
        <Field
          id="password"
          label={t("common:fields.password")}
          error={errors.password?.message}
          hint={t("common:passwordHint")}
        >
          <Input
            {...fieldA11y("password", errors.password?.message)}
            type="password"
            autoComplete="new-password"
            {...form.register("password")}
          />
        </Field>
        <Button type="submit" disabled={setup.isPending}>
          {t("setup.submit")}
        </Button>
      </form>
    </PublicLayout>
  );
}
